import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { App, Collapse, Tag, Typography } from 'antd';
import { useAuth } from '@clerk/clerk-react';
import { TraceMode, VoiceSessionEndReason } from '@evaltick/shared';

import { ROUTES } from '@/routes';
import { usePageBackLink } from '@/hooks';
import {
  AdminService,
  type AdminTraceDetail as AdminTraceDetailData,
  type AdminTraceSpan,
  type AdminTraceSpanDetail,
  type TraceStatus,
} from '@/services/adminService';
import { formatDateTime, formatDuration } from '@/utils/formatters';
import styles from './AdminTraceDetail.module.scss';

const STATUS_TAG_COLORS: Record<TraceStatus, string> = {
  success: 'success',
  error: 'error',
  running: 'processing',
};

// Horizontal indent applied per nesting level in the span tree (px).
const SPAN_INDENT_PX = 24;

const computeStatus = (span: Pick<AdminTraceSpan, 'error' | 'endedAt'>): TraceStatus => {
  if (span.error) return 'error';
  if (!span.endedAt) return 'running';
  return 'success';
};

interface SpanNode {
  span: AdminTraceSpan;
  depth: number;
}

const buildSpanOrder = (spans: AdminTraceSpan[]): SpanNode[] => {
  const bySpanId = new Map(spans.map((s) => [s.spanId, s]));
  const depthCache = new Map<string, number>();

  const getDepth = (spanId: string): number => {
    const cached = depthCache.get(spanId);
    if (cached !== undefined) return cached;
    const span = bySpanId.get(spanId);
    const parent = span?.parentSpanId ? bySpanId.get(span.parentSpanId) : undefined;
    const depth = parent ? getDepth(parent.spanId) + 1 : 0;
    depthCache.set(spanId, depth);
    return depth;
  };

  return [...spans]
    .sort((a, b) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime())
    .map((span) => ({ span, depth: getDepth(span.spanId) }));
};

const SpanRow: React.FC<{
  span: AdminTraceSpan;
  depth: number;
  detail?: AdminTraceSpanDetail;
  loadingDetail: boolean;
  onExpand: (spanId: string) => void;
}> = ({ span, depth, detail, loadingDetail, onExpand }) => {
  const status = computeStatus(span);

  return (
    <div className={styles.spanRow} style={{ marginLeft: depth * SPAN_INDENT_PX }}>
      <Collapse
        ghost
        size="small"
        onChange={(keys) => {
          if (keys.length > 0) onExpand(span.spanId);
        }}
        items={[
          {
            key: 'detail',
            label: (
              <div className={styles.spanLabel}>
                <Tag color={STATUS_TAG_COLORS[status]}>{span.spanType}</Tag>
                <Typography.Text strong>{span.name}</Typography.Text>
                {span.entityName && (
                  <Typography.Text type="secondary">{span.entityName}</Typography.Text>
                )}
                <Typography.Text type="secondary" className={styles.spanMeta}>
                  {formatDuration(span.startedAt, span.endedAt)}
                </Typography.Text>
              </div>
            ),
            children: loadingDetail ? (
              <Typography.Text type="secondary">Loading…</Typography.Text>
            ) : detail ? (
              <div className={styles.spanDetail}>
                {span.error != null && (
                  <div>
                    <Typography.Text type="danger" strong>Error</Typography.Text>
                    <pre className={styles.pre}>{JSON.stringify(span.error, null, 2)}</pre>
                  </div>
                )}
                <div>
                  <Typography.Text strong>Input</Typography.Text>
                  <pre className={styles.pre}>{JSON.stringify(detail.input, null, 2) ?? '—'}</pre>
                </div>
                <div>
                  <Typography.Text strong>Output</Typography.Text>
                  <pre className={styles.pre}>{JSON.stringify(detail.output, null, 2) ?? '—'}</pre>
                </div>
              </div>
            ) : null,
          },
        ]}
      />
    </div>
  );
};

export const AdminTraceDetail: React.FC = () => {
  const { traceId } = useParams<{ traceId: string }>();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { notification } = App.useApp();

  const [trace, setTrace] = useState<AdminTraceDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [spanDetails, setSpanDetails] = useState<Record<string, AdminTraceSpanDetail>>({});
  const [loadingSpanIds, setLoadingSpanIds] = useState<Set<string>>(new Set());

  const fetchTrace = useCallback(async () => {
    if (!traceId) return;
    setLoading(true);
    try {
      const token = await getToken();
      if (!token) return;
      const data = await new AdminService(token).getTraceDetail(traceId);
      setTrace(data);
    } catch {
      notification.error({ message: 'Failed to load trace' });
    } finally {
      setLoading(false);
    }
  }, [traceId, getToken, notification]);

  useEffect(() => {
    fetchTrace();
  }, [fetchTrace]);

  usePageBackLink(() => navigate(ROUTES.ADMIN_TRACES), 'Back to Traces');

  const handleExpandSpan = useCallback(
    async (spanId: string) => {
      if (!traceId || spanDetails[spanId] || loadingSpanIds.has(spanId)) return;
      setLoadingSpanIds((prev) => new Set(prev).add(spanId));
      try {
        const token = await getToken();
        if (!token) return;
        const detail = await new AdminService(token).getTraceSpan(traceId, spanId);
        setSpanDetails((prev) => ({ ...prev, [spanId]: detail }));
      } catch {
        notification.error({ message: 'Failed to load span detail' });
      } finally {
        setLoadingSpanIds((prev) => {
          const next = new Set(prev);
          next.delete(spanId);
          return next;
        });
      }
    },
    [traceId, getToken, notification, spanDetails, loadingSpanIds],
  );

  if (loading) {
    return (
      <div className={styles.page}>
        <Typography.Text type="secondary">Loading...</Typography.Text>
      </div>
    );
  }

  if (!trace) return null;

  const rootSpan = trace.spans.find((s) => !s.parentSpanId) ?? trace.spans[0];
  const rootStatus = rootSpan ? computeStatus(rootSpan) : undefined;
  const spanOrder = buildSpanOrder(trace.spans);

  return (
    <div className={styles.page}>
      {rootSpan && (
        <div className={styles.meta}>
          <Typography.Title level={2} className={styles.title}>
            {trace.competencyId ?? rootSpan.name}
          </Typography.Title>
          <Typography.Text type="secondary" className={styles.pageSub}>
            {rootSpan.entityName && <>{rootSpan.entityName} &middot; </>}
            Started {formatDateTime(rootSpan.startedAt)} &middot;{' '}
            {formatDuration(rootSpan.startedAt, rootSpan.endedAt)}
            {trace.callId && <> &middot; Call {trace.callId}</>}
          </Typography.Text>
          <div className={styles.metaTags}>
            {rootStatus && <Tag color={STATUS_TAG_COLORS[rootStatus]}>{rootStatus}</Tag>}
            {trace.mode === TraceMode.Voice && <Tag color="purple">Voice</Tag>}
            {trace.questionType && <Tag>{trace.questionType}</Tag>}
            {trace.phase && <Tag>{trace.phase}</Tag>}
            {trace.endReason && (
              <Tag
                color={
                  trace.endReason === VoiceSessionEndReason.LengthCap ? 'warning' : 'default'
                }
              >
                {trace.endReason === VoiceSessionEndReason.LengthCap
                  ? 'Ended: length cap'
                  : 'Ended: natural'}
              </Tag>
            )}
          </div>
          {trace.questionText && (
            <Typography.Paragraph className={styles.question}>
              {trace.questionText}
            </Typography.Paragraph>
          )}
        </div>
      )}

      <div className={styles.spans}>
        {spanOrder.map(({ span, depth }) => (
          <SpanRow
            key={span.spanId}
            span={span}
            depth={depth}
            detail={spanDetails[span.spanId]}
            loadingDetail={loadingSpanIds.has(span.spanId)}
            onExpand={handleExpandSpan}
          />
        ))}
      </div>
    </div>
  );
};

export default AdminTraceDetail;
